"use client";

import React, {useMemo, useState} from "react";
import Button from "@/app/pwa/components/Button";
import ChevronUp from "@/assets/icon/chevron-up.svg";
import ChevronDown from "@/assets/icon/chevron-down.svg";
import IconPlus from "@/assets/icon/plus.svg";

import {PlayerCardSmall} from "@/app/pwa/components/player/PlayerCardSmall";
import {usePlayerModal} from "@/hooks/usePlayerModal";
import {
  SquadPosition,
  SquadPositionData,
  SquadPositionPlayer,
} from "@/sources/kicker-amateur/types";
import {InvitationOptionsModal} from "@/app/pwa/components/modals/InvitationOptionsModal";
import {useAbility} from "@/app/pwa/lib/ability-context";
import {subject} from "@casl/ability";
import {UserInfo} from "@/sources/kicker-ovsyndication";
import {useRouter} from "next/navigation";
import {Card} from "@/app/pwa/components/Card";
import {pwaTeamMemberCreatePath, pwaTeamPlayerTransferInPath, pwaTeamPlayerTransferOutPath} from "@/utils/urlHelper";
import {
  TeamWidgetTeaser
} from "@/app/pwa/(main-layout)/team/[teamId]/(simple-layout)/widgets/components/TeamWidgetTeaser";
import NoContent from "@/app/pwa/components/NoContent";
import Link from "next/link";

const getPositionId = (position: SquadPosition) => {
  if (position.Position === "Trainer") {
    return 39;
  }

  return position.Id;
};

const Position = React.memo(
  ({
     position,
     teamId,
     onPlayerInvite,
     alreadyInvitedPeople,
   }: {
    position: SquadPosition;
    teamId: number;
    onPlayerInvite: (player: SquadPositionPlayer) => void;
    alreadyInvitedPeople: { [amaPersonId: number]: boolean };
  }) => {
    const [loading, setLoading] = useState(false)
    const router = useRouter()
    const title = useMemo(() => {
      return `${position.Position === "Unknown" ? "Ohne Position" : position.Position} (${position.Players.length})`;
    }, [position]);
    const correctedPositionId = getPositionId(position)

    const ability = useAbility();
    const canManage = ability.can("manage", subject("Team", {id: teamId.toString()}));

    return (
      <>
        {(position.Players.length > 0 || canManage) &&
          <Card p={false} className="mt-6">
            <div className="flex justify-between border-b border-gray-150 pl-4 items-center">
              <h5 className={`font-bold text-lg py-2`}>{title}</h5>
              {canManage && (
                <Button
                  color="link-secondary"
                  loading={loading}
                  onClick={() => {
                    setLoading(true)
                    router.push(pwaTeamMemberCreatePath(teamId, correctedPositionId))
                  }}
                >
                  <IconPlus/>
                </Button>
              )}
            </div>
            <div className="p-4 grid @xl/card:grid-cols-2 @4xl/card:grid-cols-3 @6xl/card:grid-cols-4 gap-4">
              {position.Players.sort((a, b) => {
                return a.LastName?.localeCompare(b.LastName ?? "") ?? 0
              }).map((player) => (
                <PlayerCardSmall
                  key={player.Id}
                  teamId={teamId}
                  player={player}
                  onInvite={onPlayerInvite}
                  showInvite={!alreadyInvitedPeople[player.Id]}
                />
              ))}
            </div>
          </Card>
        }
      </>
    );
  }
);
Position.displayName = "Position";

export const Positions = React.memo(
  ({
     positionsData,
     teamId,
     onPlayerInvite,
     alreadyInvitedPeople,
   }: {
    positionsData: SquadPositionData;
    teamId: number;
    onPlayerInvite: (player: SquadPositionPlayer) => void;
    alreadyInvitedPeople: { [amaPersonId: number]: boolean };
  }) => (
    <>
      {positionsData.Positions.map((position) => (
        <Position
          key={position.Id}
          position={position}
          teamId={teamId}
          onPlayerInvite={onPlayerInvite}
          alreadyInvitedPeople={alreadyInvitedPeople}
        />
      ))}
    </>
  )
);
Positions.displayName = "Positions";

interface TeamSquadPageProps {
  positionsData: SquadPositionData;
  teamId: number;
  alreadyInvitedPeople: { [amaPersonId: number]: boolean };
  user: UserInfo;
  clubId: number
}

export function TeamSquadPage({
                                positionsData,
                                teamId,
                                alreadyInvitedPeople,
                                user,
                                clubId
                              }: TeamSquadPageProps) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const {
    isOpen: isInviteOpen,
    selectedPlayer: selectedPlayerForInvite,
    openModal: openInviteModal,
    closeModal: closeInviteModal,
  } = usePlayerModal();

  const totalPlayers = useMemo(() => {
    return positionsData.Positions.reduce(
      (sum, position) => sum + position.Players.length,
      0
    );
  }, [positionsData]);

  const ability = useAbility();
  const canManage = ability.can("manage", subject("Team", {id: teamId.toString()}));

  return (
    <>
      {totalPlayers > 0 ? (
        <>
          {canManage && <div className="bg-surface rounded-lg p-2 md:flex md:gap-4 md:items-center">
            <div className="font-bold whitespace-nowrap">Kader verwalten</div>
            <div className="flex items-center gap-2 mt-1 md:flex-1 md:mt-0 flex-col sm:flex-row">
            <Button
              color="outline-dark"
              className="py-1 flex-1 justify-center w-full"
              onClick={() => {
                setLoading(true)
                router.push(pwaTeamMemberCreatePath(teamId))
              }}
            >
              <span>Vereinsmitglied dem Kader hinzufügen</span>
              <IconPlus className="flex-shrink-0 ml-blank" fill="currentColor"/>
            </Button>
            <Link href={pwaTeamPlayerTransferInPath(teamId.toString())} className="button button--outline-dark py-1 flex-1 justify-center w-full">
              <span>Transfer (Zugang / Abgang)</span>
              <ChevronUp className="flex-shrink-0 ml-blank" />
            </Link>
            </div>
          </div>}
          <Positions
            positionsData={positionsData}
            teamId={teamId}
            onPlayerInvite={openInviteModal}
            alreadyInvitedPeople={alreadyInvitedPeople}
          />
        </>
      ) : (
        <NoContent className="gap-2 text-center">
          {canManage ? (
            <>
              <h2 className=" font-condensed text-lg font-semibold">
                Der Kader ist noch leer
              </h2>
              <p className="max-w-64">
                Du kannst ganz einfach Spieler oder Trainer anlegen und dann mit
                einem Link oder QR-Code einladen.
              </p>
              <Button
                color="secondary"
                onClick={() => {
                  setLoading(true)
                  router.push(pwaTeamMemberCreatePath(teamId))
                }}
              >
                <span>Neues Teammitglied anlegen</span>
                <IconPlus className="flex-shrink-0 w-4 h-4 ml-blank"/>
              </Button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">
                Der Kader ist noch leer
              </h2>
              <p className="max-w-64">
                Wenn du den Kader erstellen möchtest, frage bitte den
                Verantwortlichen für diesen Verein.
              </p>
            </>
          )}
        </NoContent>
      )}
      {isInviteOpen && (
        <InvitationOptionsModal
          isOpen={isInviteOpen}
          team={positionsData.Team}
          onRequestClose={closeInviteModal}
          player={selectedPlayerForInvite as SquadPositionPlayer}
          user={user}
          clubId={clubId}
        />
      )}
      <TeamWidgetTeaser type="squad" teamId={teamId}/>
    </>
  );
};
